import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type AttachmentUploadDataDto,
  type AttachmentUploadDto,
  BusinessManagerRequester,
  type CmsBoletoDto,
  type CmsCompanyDto,
  type CmsNfeDto,
  type CmsProductDto,
  type CmsTicketDto,
  type ConversationTicketDto,
  type MultipleContractDto,
  type SendContestationDto,
  type SendContestationResponseDto,
  type SendMessageThreadDto,
} from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

import Alert from '@cobliteam/ui/components/Alert';

import useUser from '../useUser';

export const CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY = 'CRM_TICKETS_SCHEDULE_INSTALLATION';
export const CRM_SCHEDULING_SUMMARY_QUERY_KEY = 'CRM_SCHEDULING_SUMMARY_QUERY_KEY';

export const BUSINESS_MANAGER = 'BUSINESS_MANAGER';
export const BUSINESS_MANAGER_SCHEDULE_MULTIPLE_CONTRACTS_QUERY_KEY = 'SCHEDULE_MULTIPLE_CONTRACTS';
export const BUSINESS_MANAGER_SCHEDULE_BOLETO_DATA_QUERY_KEY = 'SCHEDULE_BOLETO_DATA';
export const BUSINESS_MANAGER_SCHEDULE_COMPANY_BOLETO_DATA_QUERY_KEY = 'SCHEDULE_COMPANY_BOLETO_DATA';
export const BUSINESS_MANAGER_SCHEDULE_PRODUCT_DATA_QUERY_KEY = 'SCHEDULE_PRODUCT_DATA';
export const BUSINESS_MANAGER_SCHEDULE_NFE_DATA_QUERY_KEY = 'SCHEDULE_NFE_DATA';
export const BUSINESS_MANAGER_SCHEDULE_BOLETO_PDF_DATA_QUERY_KEY = 'SCHEDULE_BOLETO_PDF_DATA';
export const BUSINESS_MANAGER_SCHEDULE_TICKET_DATA_QUERY_KEY = 'SCHEDULE_TICKET_DATA';
export const BUSINESS_MANAGER_CONVERSATION_TICKET_DATA_QUERY_KEY = 'CONVERSATION_TICKET_DATA';
export const BUSINESS_MANAGER_UPLOAD_FILE_QUERY_KEY = 'UPLOAD_FILE';
export const BUSINESS_MANAGER_SEND_MESSAGE_QUERY_KEY = 'SEND_MESSAGE';
export const CRM = 'CRM';
export const CRM_SHIPPING_QUERY_KEY = 'TICKETS_SHIPPING';
export const CRM_SCHEDULING_QUERY_KEY = 'TICKETS_SCHEDULING';
export const CRM_DEVOLUTION_QUERY_KEY = 'TICKETS_DEVOLUTION';

const env = Environment.getInstance().getEnvironment();
const businessManagerRequester = new BusinessManagerRequester({
  host: env.apiEfopBusinessManager,
});

const useBusinessManager = () => {
  const { getSession } = useUser();
  const fleetId: string = getSession().fleet.id;

  const getMultipleContractMenu = () => {
    return useQuery<MultipleContractDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_MULTIPLE_CONTRACTS_QUERY_KEY],
      queryFn: () => businessManagerRequester.getMultipleContracts(fleetId),
    });
  };
  const getBoletoTableData = (companyId: string) => {
    return useQuery<CmsBoletoDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_BOLETO_DATA_QUERY_KEY, companyId],
      queryFn: () => businessManagerRequester.getBoletoTableData(companyId),
      enabled: !!companyId,
    });
  };
  const getCompanyData = (companyId: string) => {
    return useQuery<CmsCompanyDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_COMPANY_BOLETO_DATA_QUERY_KEY, companyId],
      queryFn: () => businessManagerRequester.getCompanyData(companyId),
      enabled: !!companyId,
    });
  };
  const getNfeTableData = (companyId: string) => {
    return useQuery<CmsNfeDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_NFE_DATA_QUERY_KEY, companyId],
      queryFn: () => businessManagerRequester.getNfeTableData(companyId),
      enabled: !!companyId,
    });
  };
  const getTicketTableData = (companyId: string) => {
    return useQuery<CmsTicketDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_TICKET_DATA_QUERY_KEY, companyId],
      queryFn: () => businessManagerRequester.getTicketTableData(companyId),
      enabled: !!companyId,
    });
  };
  const getConversationTicketData = (companyId: string, ticketId: string) => {
    return useQuery<ConversationTicketDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_CONVERSATION_TICKET_DATA_QUERY_KEY, ticketId, companyId],
      queryFn: () => businessManagerRequester.getConversationTicketData(companyId, ticketId),
      enabled: !!companyId && !!ticketId,
    });
  };
  const useUploadFileAttachment = () => {
    return useMutation<AttachmentUploadDataDto, unknown, AttachmentUploadDto>({
      mutationFn: async (file) => businessManagerRequester.uploadFileAttachment(file),
      onError: () => Alert.error('Falha ao fazer upload do arquivo.'),
    });
  };

  const useSendMessageThread = () => {
    return useMutation<void, unknown, SendMessageThreadDto>({
      mutationFn: async (message) => businessManagerRequester.sendMessageToThread(message),
      onError: () => Alert.error('Falha ao enviar mensagem.'),
    });
  };
  const useContestation = () => {
    return useMutation<SendContestationResponseDto, unknown, SendContestationDto>({
      mutationFn: async (contestation) => businessManagerRequester.sendContestation(contestation),
      onError: () => Alert.error('Falha ao enviar contestação.'),
    });
  };
  const getProductData = (companyId: string) => {
    return useQuery<CmsProductDto>({
      queryKey: [BUSINESS_MANAGER, BUSINESS_MANAGER_SCHEDULE_PRODUCT_DATA_QUERY_KEY, companyId],
      queryFn: () => businessManagerRequester.getProductData(companyId),
      enabled: !!companyId,
    });
  };
  const useGetBoletoPdf = () => {
    return useMutation({
      mutationFn: async (idDoc: string) => businessManagerRequester.getBoletoPdfData(idDoc),
      onError: () => Alert.error('Falha ao fazer baixar o arquivo.'),
    });
  };

  return {
    getMultipleContractMenu,
    getBoletoTableData,
    getTicketTableData,
    getConversationTicketData,
    useUploadFileAttachment,
    useSendMessageThread,
    useQueryClient,
    getNfeTableData,
    useGetBoletoPdf,
    getCompanyData,
    getProductData,
    useContestation,
  };
};
export default useBusinessManager;
